import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { STUDIO_TRIAL_DAYS } from '@studio/contracts';
import { supabase } from '@/lib/supabase';
import { GoogleMark, Mast } from '@/components/sign-in-card';
import { CodeForm } from '@/routes/sign-in';

type Step = 'email' | 'code';

/**
 * The door that makes accounts: Google, or an email address that receives a code. The identity is all
 * that is created here; the workspace is founded on /welcome once the session exists.
 */
export function SignUpPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const google = async (): Promise<void> => {
    setError(null);
    const { error: err } = await supabase().auth.signInWithOAuth({ provider: 'google', options: { redirectTo: `${location.origin}/auth/callback` } });
    if (err) setError(err.message);
  };

  const sendCode = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { error: err } = await supabase().auth.signInWithOtp({ email, options: { shouldCreateUser: true, emailRedirectTo: `${location.origin}/auth/callback` } });
    setBusy(false);
    if (err) setError(err.message);
    else setStep('code');
  };

  const verify = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { error: err } = await supabase().auth.verifyOtp({ email, token: code, type: 'email' });
    setBusy(false);
    if (err) setError(err.message);
    else await navigate({ to: '/' });
  };

  return (
    <main className="signin">
      <div className="card">
        <Mast />
        <div>
          <h1>Create an account</h1>
          <div className="lead">{STUDIO_TRIAL_DAYS} days to try everything, no card needed.</div>
        </div>
        <button type="button" className="btn lg" onClick={() => void google()}>
          <GoogleMark />
          Continue with Google
        </button>
        <div className="divider">or</div>
        {step === 'email' ? (
          <form className="stack" onSubmit={(e) => void sendCode(e)}>
            <label className="field lg">
              <input type="email" required autoComplete="email" placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} aria-label="Email address" />
            </label>
            <button type="submit" className="btn lg primary" disabled={busy || !email}>Email me a code</button>
          </form>
        ) : (
          <CodeForm email={email} code={code} setCode={setCode} busy={busy} onSubmit={verify} onBack={() => setStep('email')} />
        )}
        {error && <div className="error" role="alert">{error}</div>}
        <p className="note">Already have an account? <Link to="/sign-in">Sign in</Link></p>
      </div>
    </main>
  );
}
